<script>import * as d3 from "d3";
import { afterUpdate, onMount } from "svelte";
import TlmdDomainCaption from "../../TlmdDomainCaption.svelte";
import ObservationTable from "../../d3/ObservationTable.svelte";
import BarPieChart from "../../d3/bar-pie-chart/BarPieChart.svelte";
import ReportConfigurator from "../../report/Configurator.svelte";
import FrequencyPlotConfigurator from "../../d3/frequency-plot/Configurator.svelte";
import ChartConfigurator from "../../d3/bar-pie-chart/Configurator.svelte";
import DataTableConfigurator from "../../d3/data-table/Configurator.svelte";
import Button from "../../../components/Button.svelte";
import FrequencySection from "./FrequencySection.svelte";
import FrequencyBarPlot from "../../d3/frequency-plot/FrequencyBarPlot.svelte";
import { API } from "../../../api/api.js";
import {
  locale as globalLocale,
  timeZone as globalTimeZone,
  timeRange
} from "../global-store.js";
import {
  IntervalBase
} from "../../d3/frequency-plot/types.js";
import {
  extractPropertyMap
} from "../../d3/legacy/observation/list.js";
import { extractStatisticsConfiguration } from "../../d3/legacy/observation/process-definition.js";
import { getPieChartSectionData } from "../../d3/legacy/observation/statistics.js";
import { theme } from "../../../stores/theme.js";
import { okhsl } from "../../../d3-mapp/color/index.js";
import ReportFooter from "../ReportFooter.svelte";
import ReportSection from "../ReportSection.svelte";
import { humanTimeZone } from "../../util/i18n-time-util.js";
import { getDateTimeFormatters } from "../../d3/time-axis/timeline-scale.js";
export let domain;
export let domainExtName;
export let timeZone = void 0;
export let locale = void 0;
export let rangeStart;
export let rangeEnd;
export let rangeType;
export let fixedRangeType = false;
export let host = void 0;
export let credentials = void 0;
export let assets = "";
export let observationTypeId;
let mounted = false;
let started = false;
let dataRange = void 0;
let headerElem;
let headerHeight = 0;
afterUpdate(() => {
  const height = headerElem ? headerElem.getBoundingClientRect().height : 0;
  if (height !== headerHeight) {
    headerHeight = height;
  }
});
$timeRange = { start: new Date(rangeStart), end: new Date(rangeEnd) };
$globalLocale = locale ?? $globalLocale;
$globalTimeZone = timeZone ?? $globalTimeZone;
$:
  reportConfiguration = {
    range: {
      start: $timeRange.start.getTime(),
      end: $timeRange.end.getTime(),
      type: rangeType
    }
  };
$:
  formatters = getDateTimeFormatters(
    $globalLocale,
    $globalTimeZone,
    "hours"
  );
$:
  startMarker = {
    millis: $timeRange.start.getTime(),
    label: formatters.bound($timeRange.start)
  };
$:
  endMarker = {
    millis: $timeRange.end.getTime(),
    label: formatters.bound($timeRange.end)
  };
$:
  rangeAsText = `${startMarker?.label ?? $timeRange.start} - ${endMarker?.label ?? $timeRange.end}`;
let observationType;
let observationData = [];
let frequencyPlotConfiguration = {
  visible: true,
  interval: { value: 1, unit: "auto" }
};
let plotDefinition = {
  type: FrequencyBarPlot,
  properties: {
    interval: "auto",
    data: [],
    title: "",
    yAxisLabel: "",
    color: okhsl("hsla(300,50%,50%,1.0)"),
    padding: 0.1,
    showAxis: true
  }
};
let chartConfigurations = [];
let chartSectionIndex = -1;
let chartIndex = -1;
let chartConfiguration = { type: "auto", visible: true };
let chartName = "";
let dataTableConfiguration = {
  visible: true
};
$: {
  if (reportConfiguration.range.end !== reportConfiguration.range.start) {
    getData(reportConfiguration.range);
  }
}
$: {
  if (frequencyPlotConfiguration.interval.unit === "auto") {
    plotDefinition.properties.interval = "auto";
  } else {
    const count = frequencyPlotConfiguration.interval.value;
    let interval = null;
    switch (frequencyPlotConfiguration.interval.unit) {
      case IntervalBase.HOURS:
        interval = d3.timeHour;
        if (interval && count !== void 0 && count > 1) {
          interval = interval.every(count);
        }
        break;
      case IntervalBase.DAYS:
        interval = d3.timeDay;
        if (interval && count !== void 0 && count > 1) {
          interval = interval.every(count);
        }
        break;
      case IntervalBase.WEEKS:
        interval = d3.timeDay;
        if (interval) {
          interval = interval.every(
            (count !== void 0 && count > 1 ? count : 1) * 7
          );
        }
        break;
      case IntervalBase.MONTHS:
        interval = d3.timeMonth;
        if (interval && count !== void 0 && count > 1) {
          interval = interval.every(count);
        }
        break;
      case IntervalBase.YEARS:
        interval = d3.timeYear;
        if (interval && count !== void 0 && count > 1) {
          interval = interval.every(count);
        }
        break;
    }
    plotDefinition.properties.interval = interval ?? "auto";
  }
}
const getData = async (range) => {
  const doGetData = started && range && range.start !== void 0 && range.end !== void 0 && (dataRange == void 0 || dataRange.start != range.start || dataRange.end != range.end);
  if (doGetData) {
    dataRange = range;
    observationData = observationTypeId ? await API.getObservations(
      new Date(range.start).toISOString(),
      new Date(range.end).toISOString(),
      [observationTypeId]
    ) : [];
    plotDefinition.properties.data = observationData;
  }
};
$:
  reportTitle = getReportTitle(observationType);
onMount(async () => {
  mounted = true;
  try {
    console.log("Ready...");
    API.init(host ? host : "");
    API.setDomain(domain);
    if (credentials) {
      API.setCredentials(credentials.usr, credentials.pwd);
    } else {
      API.reloadCredentials();
    }
    await API.signIn();
    observationType = await API.getObservationTypeDefinition(observationTypeId);
    statisticsConfiguration = extractStatisticsConfiguration(observationType);
    propertiesMap = extractPropertyMap(observationType);
    plotDefinition.properties.color = okhsl(observationType.properties.color);
    plotDefinition.properties.title = observationType.schema.description;
    plotDefinition.properties.yAxisLabel = statisticsConfiguration?.labels.items ?? "occurrences";
    itemsLabel = statisticsConfiguration?.labels.items ?? itemsLabel;
    noItemsLabel = statisticsConfiguration?.labels.noItems ?? noItemsLabel;
    started = true;
    await getData(reportConfiguration.range);
  } catch (error) {
    console.log("ERROR", error);
  }
});
let itemsLabel = "Data point(s).";
let noItemsLabel = "No data.";
let statisticsConfiguration = void 0;
let pieChartSectionData = [];
let propertiesMap = void 0;
const pieSize = 480 / 3;
const pieRatio = 3;
const getLabelFull = (datum) => datum ? `<tspan>${datum.label}</tspan><tspan x="0" dy="1.2em">${(datum.value * 100).toFixed(
  1
)}% | ${datum.count} ${plotDefinition.properties.yAxisLabel}</tspan>` : "";
const getLabelMedium = (datum) => datum ? `${datum.label} (${(datum.value * 100).toFixed(1)}%)` : "";
const getLabelShort = (datum) => datum ? datum.label : "";
const getValue = (datum) => datum ? datum.count : 0;
$: {
  if (statisticsConfiguration) {
    pieChartSectionData = getPieChartSectionData(
      observationData,
      statisticsConfiguration,
      true
      //return all possible filter settings
    );
    chartConfigurations = new Array(pieChartSectionData.length);
    for (let pcsi = 0; pcsi < pieChartSectionData.length; pcsi++) {
      chartConfigurations[pcsi] = pieChartSectionData[pcsi].sectionData.map(
        (pieChartData) => {
          return { type: "auto", visible: true };
        }
      );
    }
  }
}
const updateChartConfiguration = (newConfig) => {
  Object.assign(chartConfiguration, newConfig);
  chartConfigurations = chartConfigurations.concat([]);
};
const openProperties = (key) => {
  const propertiesDiv = document.querySelector(".report div.properties");
  propertiesDiv.classList.remove("closed");
  let configuratorClassName = key;
  if (key.startsWith("chart")) {
    const keyParts = key.split("-");
    configuratorClassName = keyParts[0];
    chartSectionIndex = parseInt(keyParts[1]);
    chartIndex = parseInt(keyParts[2]);
    chartConfiguration = chartConfigurations[chartSectionIndex][chartIndex];
    chartName = pieChartSectionData[chartSectionIndex].sectionData[chartIndex].title;
  }
  propertiesDiv.querySelectorAll("div.configurator").forEach((elem) => {
    if (elem.classList.contains(configuratorClassName)) {
      elem.classList.add("open");
    } else {
      elem.classList.remove("open");
    }
  });
};
const closeProperties = () => {
  const propertiesDiv = document.querySelector(".report div.properties");
  document.querySelector(".report div.properties")?.classList.add("closed");
};
const getReportTitle = (observationType2) => {
  if (observationType2) {
    let title = observationType2.schema.title;
    return title;
  }
  return "...";
};
</script>

<svelte:head>
  <title>{reportTitle} Report - {rangeAsText}</title>
</svelte:head>
<!--use this test to avoid seeing light theme first while dark has been chosen (the flicker of brightness) -->
{#if mounted}
  <div class="report {$theme}" style="--headerHeight:{headerHeight}px;">
    <div class="content">
      <header bind:this={headerElem} class="header">
        <!--this will not be printed-->
        <div class="caption">
          <div class="noprint float">
            <Button
              story={"button.primary.medium"}
              on:click={(ev) => window.print()}
              ><span class="glyph-print" /></Button
            >
            <Button
              story={$theme === "dark"
                ? "button.secondary.medium.icon"
                : "button.primary.medium.icon"}
              on:click={(ev) => openProperties("general")}
              ><span class="glyph-cog" /></Button
            >
          </div>
          <TlmdDomainCaption {assets} domain={domainExtName} /> - {observationType
            ?.schema.title ?? "Loading"} Report
        </div>
        <div class="info">
          {rangeAsText} ({humanTimeZone($globalTimeZone, $globalLocale)})
        </div>
        <div class="info">
          {observationData && observationData.length > 0
            ? `${observationData.length} ${itemsLabel}`
            : noItemsLabel}
        </div>
      </header>
      <div class="report-body tlmd-scroll">
        <table class="page">
          <thead
            ><!--this will not be visible on screen--><tr
              ><th>
                <header bind:this={headerElem} class="header">
                  <!--this will not be printed-->
                  <div class="caption">
                    <div class="noprint float">
                      <Button
                        story={"button.primary.medium"}
                        on:click={(ev) => window.print()}
                        ><span class="glyph-print" /></Button
                      >
                      <Button
                        story={$theme === "dark"
                          ? "button.secondary.medium.icon"
                          : "button.primary.medium.icon"}
                        on:click={(ev) => openProperties("general")}
                        ><span class="glyph-cog" /></Button
                      >
                    </div>
                    <TlmdDomainCaption {assets} domain={domainExtName} /> - {observationType
                      ?.schema.title ?? "Loading"} Report
                  </div>
                  <div class="info">
                    {rangeAsText} ({humanTimeZone(
                      $globalTimeZone,
                      $globalLocale
                    )})
                  </div>
                  <div class="info">
                    {observationData && observationData.length > 0
                      ? `${observationData.length} ${itemsLabel}`
                      : noItemsLabel}
                  </div>
                </header>
              </th></tr
            ></thead
          >
          <tbody>
            <ReportSection
              noprint={!frequencyPlotConfiguration.visible}
              onProperties={() => openProperties("frequency")}
            >
              <FrequencySection {plotDefinition} />
            </ReportSection>
            <!--tr
              class="section {frequencyPlotConfiguration.visible
                ? ''
                : 'noprint'}"
              ><td>
                <div class="time-plot">
                  <div class="noprint float">
                    <Button
                      story={$theme === "dark"
                        ? "button.secondary.medium.icon"
                        : "button.primary.medium.icon"}
                      on:click={(ev) => openProperties("frequency")}
                      ><span class="glyph-cog" /></Button
                    >
                  </div>
                  <div
                    class={frequencyPlotConfiguration.visible
                      ? ""
                      : "not-included"}
                  >
                    <TimePlot
                      start={reportConfiguration.range.start}
                      end={reportConfiguration.range.end}
                      plotDefinitions={[plotDefinition]}
                      timeZone={"Europe/Amsterdam"}
                      locale={$globalLocale}
                      role={"plot"}
                      bind:startMarker
                      bind:endMarker
                    />
                  </div>
                </div>
              </td></tr
            -->
            {#each chartConfigurations as section, i}
              <tr class="section">
                <td>
                  <div class="center">
                    <div class="chart-section">
                      <div class="header">{pieChartSectionData[i].title}</div>
                      <div class="charts">
                        {#each section as chartConfig, j}
                          {#if pieChartSectionData[i].sectionData[j] !== undefined}
                            <div
                              class="chart statistics pie {chartConfig.visible
                                ? ''
                                : 'noprint'}"
                            >
                              <div class="noprint float">
                                <Button
                                  story={$theme === "dark"
                                    ? "button.secondary.medium.icon"
                                    : "button.primary.medium.icon"}
                                  on:click={(ev) =>
                                    openProperties(`chart-${i}-${j}`)}
                                  ><span class="glyph-cog" /></Button
                                >
                              </div>
                              <div
                                class="header {chartConfig.visible
                                  ? ''
                                  : 'not-included'}"
                              >
                                {pieChartSectionData[i].sectionData[j].title}
                              </div>
                              <div
                                class={chartConfig.visible
                                  ? ""
                                  : "not-included"}
                              >
                                <BarPieChart
                                  tag={"pie-chart"}
                                  size={pieSize}
                                  aspectRatio={pieRatio}
                                  data={pieChartSectionData[i].sectionData[j]}
                                  label={getLabelMedium}
                                  value={getValue}
                                  axisUnit={itemsLabel}
                                  type={chartConfig.type}
                                  onLabel={(datum) => datum.count.toString()}
                                  centerLabel={(data) => data.count.toString()}
                                />
                              </div>
                            </div>
                          {/if}
                        {/each}
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            {/each}
            <tr
              class="section {dataTableConfiguration.visible ? '' : 'noprint'}"
              ><td>
                <div class="data">
                  <div class="noprint float">
                    <Button
                      story={$theme === "dark"
                        ? "button.secondary.medium.icon"
                        : "button.primary.medium.icon"}
                      on:click={(ev) => openProperties("data")}
                      ><span class="glyph-cog" /></Button
                    >
                  </div>
                  <div
                    class={dataTableConfiguration.visible ? "" : "not-included"}
                  >
                    <ObservationTable {propertiesMap} {observationData} />
                  </div>
                </div>
              </td></tr
            >
          </tbody>
          <ReportFooter />
        </table>
      </div>
    </div>
    <div class="noprint properties right closed">
      <div class="noprint float close">
        <Button
          story={$theme === "dark"
            ? "button.secondary.medium.icon"
            : "button.primary.medium.icon"}
          on:click={closeProperties}><span class="glyph-x" /></Button
        >
      </div>
      <div class="header">
        <span class="caption">Properties</span>
        <!--ThemeSwitch /-->
      </div>
      <div class="configurator general open">
        <ReportConfigurator bind:value={reportConfiguration} {fixedRangeType} />
      </div>
      <div class="configurator frequency">
        <FrequencyPlotConfigurator bind:value={frequencyPlotConfiguration} />
      </div>
      <div class="configurator chart">
        {#if chartConfigurations && chartConfigurations.length > 0 && chartSectionIndex >= 0 && chartConfigurations[chartSectionIndex].length > 0 && chartIndex >= 0}
          <ChartConfigurator
            value={chartConfiguration}
            name={chartName}
            on:changed={(ev) => updateChartConfiguration(ev.detail)}
          />
        {/if}
      </div>
      <div class="configurator data">
        <DataTableConfigurator bind:value={dataTableConfiguration} />
      </div>
    </div>
  </div>
{/if}

<style>
  div.report {
    --report-caption-font-size: 24px;
    --report-header-info-font-size: 14px;
    --report-section-header-font-size: 18px;
    --report-chart-header-font-size: 16px;
    --report-footer-font-size: 14px;

    --base-color-bg-default: #2d2f36;
    --base-color-primary-default: #6bae85;
    --base-color-onSurface-secondary: #1a1b1f;
    --base-color-onSurface-primary: #dbe4e7;
    --core-color-crayon-honeyYellow: #fbc62f;
  }

  div.report {
    --surface-color: var(--base-color-onSurface-primary);
    --header-surface-color: var(--core-color-crayon-honeyYellow);
    --header-onSurface-color: var(--base-color-onSurface-secondary);
    --section-onSurface-color: var(--base-color-onSurface-secondary);
    --data-header-onSurface-color: var(--base-color-onSurface-primary);
    --scroll-color: var(--base-color-onSurface-secondary);
  }

  div.report.dark {
    --surface-color: var(--base-color-bg-default);
    --header-surface-color: var(--base-color-onSurface-secondary);
    --header-onSurface-color: var(--base-color-primary-default);
    --section-onSurface-color: var(--base-color-onSurface-primary);
    --data-header-onSurface-color: var(--base-color-bg-default);
    --scroll-color: var(--base-color-primary-default);
  }
  @media print {
    div.report.dark,
    div.report {
      --surface-color: transparent;
      --header-surface-color: transparent;
      --header-onSurface-color: var(--base-color-onSurface-secondary);
      --section-onSurface-color: var(--base-color-onSurface-secondary);
      --data-header-onSurface-color: white;
    }
  }

  .float {
    float: inline-end;
    position: relative;
    z-index: 10000;
  }
  div.properties {
    border-color: var(--section-onSurface-color);
  }
  div.properties.right {
    border-left: 1px solid var(--header-surface-color);
  }
  div.properties.right:not(.closed) {
    min-width: 425px;
  }
  div.properties.top {
    border-bottom: 1px solid var(--header-surface-color);
  }
  div.properties.closed {
    width: 0px;
    border: none;
  }
  div.properties.closed :global(*) {
    position: absolute;
    left: -9999px;
  }

  div.properties div.header {
    background-color: var(--header-surface-color);
    border-left: 2px solid var(--surface-color);
    padding: 1em;
    color: var(--section-onSurface-color);
  }
  div.properties div.header .caption {
    font-weight: 700;
    font-size: 18px;
    margin: 1em 0;
    display: inline-block;
  }
  .report header.header {
    margin-bottom: 1rem;
  }

  div.properties .configurator {
    color: var(--section-onSurface-color);
  }

  div.properties div.configurator {
    display: none;
  }
  div.properties div.configurator.open {
    display: block;
  }

  div.properties.closed div.close {
    display: none;
  }

  div.report {
    background-color: var(--surface-color);
  }

  .report table {
    border: 0;
    border-collapse: collapse;
    width: 100%;
  }

  .report .content > header,
  .report table thead {
    background-color: var(--header-surface-color);
  }
  .report table thead tr th {
    font-weight: normal;
    text-align: unset;
  }

  div.report :global(.tlmd-domain-caption) {
    --domain-font-size: var(--report-caption-font-size);
  }

  .report .content > header,
  .report table thead tr th .header {
    color: var(--header-onSurface-color);
  }
  .report .content > header .caption,
  .report table thead tr th .header .caption {
    font-size: var(--report-caption-font-size);
    font-weight: bold;
  }
  .report .content > header .info,
  .report table thead tr th .header .info {
    font-size: var(--report-header-info-font-size);
    font-weight: bold;
    margin-top: 0.5em;
  }

  .report table tbody tr.section td {
    color: var(--section-onSurface-color);
  }

  .report table tbody tr.section td .header {
    font-size: var(--report-section-header-font-size);
    font-weight: bold;
  }
  .report table tbody tr.section td .chart .header {
    font-size: var(--report-chart-header-font-size);
    font-weight: bold;
  }

  .report table tfoot tr td {
    color: var(--section-onSurface-color);
  }

  .report .content > header,
  .report table thead th {
    padding: 1em;
  }
  .report table tfoot td {
    padding: 1em;
  }
  .report table tbody td {
    padding: 0 1em 1em 1em;
  }
  .report {
    font-family: Roboto;
  }

  div.statistics.pie {
    display: inline-block;
    min-width: 360px;
    width: calc(50% - 1em);
    max-width: 640px;
    margin: 0.5em;
    border: 1px solid var(--section-onSurface-color);
    border-radius: 0.5em;
    padding: 1em;
  }
  .report table tbody td :global(svg) {
    color: var(--section-onSurface-color);
  }

  /* .report div.distribution {
		text-align: center;
	} */

  .report table tbody td div.data :global(table thead) {
    background-color: var(--section-onSurface-color);
    color: var(--data-header-onSurface-color);
  }
  .report table tbody td div.data :global(thead th) {
    border-left: 1px solid var(--data-header-onSurface-color);
  }

  /* tr.section td div.center div.chart-section [ .header, div.charts CHART]*/

  @media screen {
    .report {
      display: grid;
    }

    .report {
      grid-template-columns: 1fr auto;
    }

    .report > div.content > div.report-body {
      max-height: calc(100vh - var(--headerHeight) - 0px);
      overflow-y: auto;
    }

    /* Styling the scrollbar in Firefox  */
    .tlmd-scroll {
      scrollbar-color: var(--scroll-color) transparent;
      scrollbar-width: thin;
    }
    /* Styling the scrollbar in Chrome and Safari */
    .tlmd-scroll::-webkit-scrollbar {
      width: 3px;
    }
    .tlmd-scroll::-webkit-scrollbar-thumb {
      background-color: var(
        --scroll-color
      ); /* Set thumb color for Chrome and Safari */
    }
    .tlmd-scroll::-webkit-scrollbar-track {
      background-color: transparent; /* Set track color for Chrome and Safari */
    }

    .report .content {
      grid-column: 1 / 2;
    }

    .report .properties {
      grid-column: 2 / 3;
    }

    .report table :global(svg.plot) {
      max-width: 1292px;
    }
    .report table thead {
      display: none;
    }

    .not-included {
      -webkit-filter: blur(5px) grayscale(1);
      -moz-filter: blur(5px) grayscale(1);
      -o-filter: blur(5px) grayscale(1);
      -ms-filter: blur(5px) grayscale(1);
      filter: blur(5px) grayscale(1);
    }
  }

  @media print {
    .report {
      display: block;
    }
    .report .content > header {
      display: none;
    }
    .not-included,
    .noprint {
      display: none;
      position: absolute;
      border: none;
      left: -9999px;
    }
    div.statistics.pie {
      min-width: 0;
    }
    div.properties.right.closed,
    div.properties.right {
      min-width: 0;
      width: 0;
    }
  }
</style>
